/* FOM · contrato operacional do no — camada PRIVATE.

   Um contrato por no. Nunca publicado, invisivel para `viewer`.

   A assimetria que o FOM original tinha aqui foi corrigida no backend
   (D-FOM-008, decisao 3): editar contrato entra no historico E e reversivel.
   Reverter, para o servidor, e gravar de novo o corpo anterior — e por isso
   esta tela guarda o corpo carregado e oferece "desfazer" com `undo_of`, em
   vez de fingir que existe um botao magico de undo. */
import type { Api } from '../core/api.ts';
import { ApiError } from '../core/api.ts';
import type {
  FomNode, Contract, ContractBody, StatusClass, AutomationLevel,
} from '../core/types.ts';
import {
  CONTRATO_LISTAS, CONTRATO_AUTOMACAO, ROTULO_CONTRATO, ROTULO_ST,
} from '../core/types.ts';
import { el, botao, campo, area, selecao, limpar, vazio, dataCurta } from './dom.ts';

const NIVEIS: AutomationLevel[] = [
  'automático', 'semi-automático', 'humano', 'aprovação humana', 'exceção humana',
];
const ORIGENS: StatusClass[] = ['E1', 'E2', 'E3', 'E4', 'ND', 'TOBE', 'MIX'];

export type ContratoOpcoes = {
  api: Api;
  org: string;
  mapa: string;
  aoErro: (erro: unknown) => void;
};

/* O corpo do contrato viaja como listas de strings. Na tela isso vira uma
   caixa de texto por campo, uma linha por item — que e como as pessoas
   realmente escrevem, e evita um editor de listas que ninguem pediu. */
function paraTexto(v: string[] | undefined): string {
  return (v ?? []).join('\n');
}
function paraLista(t: string): string[] {
  return t.split('\n').map((l) => l.trim()).filter((l) => l.length > 0);
}

export function montarContrato(host: HTMLElement, o: ContratoOpcoes) {
  let no: FomNode | null = null;
  let atual: Contract | null = null;
  /* o corpo como estava quando esta tela o carregou — a base do "desfazer" */
  let anterior: Contract | null = null;
  let editando = false;

  async function carregar(): Promise<void> {
    if (!no) { atual = null; return; }
    try {
      atual = await o.api.contrato(o.org, o.mapa, no.external_id);
      anterior = atual;
    } catch (err) {
      /* 404 aqui nao e erro: e "este no ainda nao tem contrato" */
      if (err instanceof ApiError && err.status === 404) { atual = null; return; }
      throw err;
    }
  }

  /* ------------------------------------------------------------ leitura -- */

  function renderLeitura(): void {
    const cab = el('div', 'gaveta-cabecalho');
    cab.appendChild(el('h3', 'gaveta-titulo', 'Contrato'));
    cab.appendChild(el('span', 'selo selo-privado', 'PRIVADO'));
    host.appendChild(cab);

    if (!no) {
      host.appendChild(vazio('Nenhum no selecionado.',
        'O contrato descreve como um no opera: entradas, decisoes, saidas, excecoes.'));
      return;
    }

    host.appendChild(el('p', 'gaveta-nota', no.name));

    if (!atual) {
      host.appendChild(vazio(
        'Este no ainda nao tem contrato.',
        'Um contrato responde: o que entra, quem decide o que, o que sai, e o que '
        + 'acontece quando algo foge do previsto.',
      ));
      host.appendChild(botao('Escrever contrato', () => { editando = true; render(); },
        'botao botao-principal'));
      return;
    }

    const meta = el('div', 'contrato-meta');
    meta.appendChild(par('Origem', ROTULO_ST[atual.origem]));
    meta.appendChild(par('Automacao', atual.automation_level ?? 'nao definida'));
    meta.appendChild(par('Candidato a automacao', atual.automation_candidate ? 'sim' : 'nao'));
    meta.appendChild(par('Atualizado', dataCurta(atual.updated_at)));
    host.appendChild(meta);

    const corpo = atual.body ?? {};
    let algum = false;
    for (const k of CONTRATO_LISTAS) {
      const v = corpo[k];
      if (!v || !v.length) continue;
      algum = true;
      host.appendChild(el('h4', 'gaveta-secao', ROTULO_CONTRATO[k] ?? k));
      const ul = el('ul', 'lista-contrato');
      for (const item of v) ul.appendChild(el('li', undefined, item));
      host.appendChild(ul);
    }
    for (const k of CONTRATO_AUTOMACAO) {
      const v = corpo[k];
      if (!v) continue;
      algum = true;
      host.appendChild(el('h4', 'gaveta-secao', ROTULO_CONTRATO[k] ?? k));
      host.appendChild(el('pre', 'contrato-bloco', v));
    }
    if (!algum) {
      host.appendChild(vazio('O contrato existe, mas esta vazio.',
        'Nenhum campo foi preenchido ainda.'));
    }

    const acoes = el('div', 'formulario-acoes');
    acoes.appendChild(botao('Editar', () => { editando = true; render(); }, 'botao'));
    acoes.appendChild(botao('Remover contrato', () => {
      void executar(async () => {
        await o.api.removerContrato(o.org, o.mapa, no!.external_id);
        atual = null;
      });
    }, 'botao botao-perigo'));
    host.appendChild(acoes);
  }

  function par(rotulo: string, valor: string): HTMLElement {
    const d = el('div', 'contrato-par');
    d.appendChild(el('span', 'campo-rotulo', rotulo));
    d.appendChild(el('span', 'campo-valor', valor));
    return d;
  }

  /* ------------------------------------------------------------- edicao -- */

  function renderEdicao(): void {
    host.appendChild(el('h3', 'gaveta-titulo', atual ? 'Editar contrato' : 'Novo contrato'));
    host.appendChild(el('p', 'gaveta-nota', no!.name));

    const form = el('form', 'formulario');
    form.noValidate = true;

    const origem = selecao(ORIGENS.map((s) => ({ valor: s, rotulo: ROTULO_ST[s] })),
      atual?.origem ?? 'E4');
    const nivel = selecao(
      [{ valor: '', rotulo: 'nao definida' }, ...NIVEIS.map((n) => ({ valor: n, rotulo: n }))],
      atual?.automation_level ?? '',
    );
    const candidato = el('input');
    candidato.type = 'checkbox';
    candidato.checked = atual?.automation_candidate ?? false;

    form.appendChild(campo('Origem do contrato', origem,
      'de onde vem a confianca no que esta escrito aqui'));
    form.appendChild(campo('Nivel de automacao', nivel));

    const linhaCand = el('label', 'campo campo-checkbox');
    linhaCand.appendChild(candidato);
    linhaCand.appendChild(el('span', 'campo-rotulo', 'candidato a automacao'));
    form.appendChild(linhaCand);

    const corpo = atual?.body ?? {};
    const listas = new Map<string, HTMLTextAreaElement>();
    const autos = new Map<string, HTMLTextAreaElement>();

    form.appendChild(el('h4', 'gaveta-secao', 'Operacao'));
    for (const k of CONTRATO_LISTAS) {
      const a = area(paraTexto(corpo[k]), 2, 'um item por linha');
      listas.set(k, a);
      form.appendChild(campo(ROTULO_CONTRATO[k] ?? k, a));
    }

    form.appendChild(el('h4', 'gaveta-secao', 'Automacao'));
    for (const k of CONTRATO_AUTOMACAO) {
      const a = area(corpo[k] ?? '', 2);
      autos.set(k, a);
      form.appendChild(campo(ROTULO_CONTRATO[k] ?? k, a));
    }

    const acoes = el('div', 'formulario-acoes');
    acoes.appendChild(botao('Cancelar', () => { editando = false; render(); }));
    const salvar = el('button', 'botao botao-principal', 'Gravar contrato');
    salvar.type = 'submit';
    acoes.appendChild(salvar);
    form.appendChild(acoes);

    form.addEventListener('submit', (ev) => {
      ev.preventDefault();
      const novo: ContractBody = {};
      for (const [k, a] of listas) {
        const v = paraLista(a.value);
        if (v.length) (novo as Record<string, unknown>)[k] = v;
      }
      for (const [k, a] of autos) {
        const v = a.value.trim();
        if (v) (novo as Record<string, unknown>)[k] = v;
      }
      void executar(async () => {
        atual = await o.api.gravarContrato(o.org, o.mapa, no!.external_id, {
          origem: origem.value as StatusClass,
          ...(nivel.value ? { automation_level: nivel.value as AutomationLevel } : {}),
          automation_candidate: candidato.checked,
          body: novo,
        });
        editando = false;
      });
    });

    host.appendChild(form);

    /* Desfazer honesto: grava de novo o corpo que esta tela carregou, marcado
       como desfazer. Nao e um undo de sessao — e uma mutacao auditavel. */
    if (anterior && atual && anterior.updated_at !== atual.updated_at) {
      host.appendChild(botao('Desfazer para a versao carregada', () => {
        void executar(async () => {
          atual = await o.api.gravarContrato(o.org, o.mapa, no!.external_id, {
            origem: anterior!.origem,
            ...(anterior!.automation_level
              ? { automation_level: anterior!.automation_level } : {}),
            automation_candidate: anterior!.automation_candidate,
            body: anterior!.body,
            undo_of: 'contrato ' + no!.external_id,
          });
          editando = false;
        });
      }, 'botao-link'));
    }
  }

  async function executar(fn: () => Promise<void>): Promise<void> {
    try {
      await fn();
      render();
    } catch (err) {
      o.aoErro(err);
      render();
    }
  }

  function render(): void {
    limpar(host);
    if (editando && no) renderEdicao();
    else renderLeitura();
  }

  function semAcesso(): void {
    limpar(host);
    host.appendChild(el('h3', 'gaveta-titulo', 'Contrato'));
    host.appendChild(vazio(
      'Camada privada.',
      'Seu papel nesta organizacao nao le contratos. Isso e decidido pelo servidor.',
    ));
  }

  return {
    async mostrar(selecionado: FomNode | null): Promise<void> {
      const mudou = selecionado?.external_id !== no?.external_id;
      no = selecionado;
      if (mudou) editando = false;
      try {
        await carregar();
        render();
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) { semAcesso(); return; }
        o.aoErro(err);
      }
    },
  };
}
